<template>
  <SideBarLayoutContent>
    <template #top-bar>
      <div class="flex w-full flex-row items-center gap-3">
        <h1 class="text-display-small grow">{{ $t('coaching.feedbackGoals.pageTitle') }}</h1>
        <BaseTextButton
          v-if="canCreateFeedbackGoal"
          color="tonal"
          size="M"
          icon="add"
          @click="openCreateDialog"
          >{{ $t('coaching.feedbackGoals.createAction') }}</BaseTextButton
        >
      </div>
    </template>
    <LoadingWrapper :state="status">
      <ul v-if="feedbackGoalsList.length" class="flex flex-col gap-0.5">
        <li
          v-for="goal in feedbackGoalsList"
          :key="goal.id"
          class="first:[&>div]:rounded-t-large last:[&>div]:rounded-b-large"
        >
          <CoachFeedbackGoalPreviewItem :goal="goal" />
        </li>
      </ul>
      <EmptyPage
        v-else
        :title="$t('coaching.feedbackGoals.empty.title')"
        :description="$t('coaching.feedbackGoals.empty.description')"
        icon="flag"
      >
        <BaseTextButton
          v-if="canCreateFeedbackGoal"
          color="tonal"
          size="M"
          icon="add"
          @click="openCreateDialog"
          >{{ $t('coaching.feedbackGoals.createAction') }}</BaseTextButton
        >
      </EmptyPage>
    </LoadingWrapper>
    <CreateCoachFeedbackGoalDialog
      :is-open="isCreateDialogOpen"
      @close-dialog="closeCreateDialog"
      @goal-created="
        () => {
          closeCreateDialog()
          reload()
        }
      "
    />
  </SideBarLayoutContent>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from 'vue'
  import { useEventBus } from '@vueuse/core'
  import SideBarLayoutContent from '@/core/components/layouts/SideBarLayoutContent.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import EmptyPage from '@/core/components/empty-states/EmptyPage.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { Permission } from '@/core/models/user'
  import { usePermission } from '@/core/composables/use-permission'
  import { EventKey } from '@/core/events/event-key'
  import { useCoachFeedbackGoals } from '../composables/use-coach-feedback-goals'
  import CoachFeedbackGoalPreviewItem from '../components/CoachFeedbackGoalPreviewItem.vue'
  import CreateCoachFeedbackGoalDialog from '../components/CreateCoachFeedbackGoalDialog.vue'

  const { can } = usePermission()
  const canCreateFeedbackGoal = computed(() => can(Permission.COACHING_CREATE_GOAL))

  const {
    isOpen: isCreateDialogOpen,
    openDialog: openCreateDialog,
    closeDialog: closeCreateDialog,
  } = useDialog()

  const { feedbackGoals, status, reload } = useCoachFeedbackGoals()
  const feedbackGoalsList = computed(() => feedbackGoals.value ?? [])

  const bus = useEventBus(EventKey.coachFeedbackGoalsUpdated)
  let stopBus: (() => void) | undefined

  onMounted(() => {
    stopBus = bus.on(() => {
      reload()
    })
  })

  onUnmounted(() => {
    stopBus?.()
  })
</script>
